/**
 * The eight narrowings, every one of them a **server** parameter.
 *
 * `web/src/audit/filters.ts` carries the argument; the short version is that this
 * endpoint pages by **cursor** and publishes no total, so a filter applied in the
 * browser would produce a count with nothing to check it against — and the person
 * reading it is writing an audit report.
 *
 * ── A draft, then an apply ─────────────────────────────────────────────────
 *
 * The fields edit a **draft**, and the request only changes when *Áp dụng* is
 * pressed. Not for performance: this read is a sensitive access that writes a
 * `wp_data_access_logs` row, so a filter that re-fired on every keystroke would fill
 * the audit log with the auditor's own typing and bury the rows somebody is looking
 * for. `useApiQuery` already refuses to depend on the header object by identity for
 * the same reason.
 *
 * ── `action` is an exact match, and the hint says so ───────────────────────
 *
 * `openapi.yaml` describes it as "Exact match on the action verb". Typing `device`
 * returns nothing, and without the hint that reads as *"no device action was ever
 * audited"* — which is the absence-as-a-measurement failure again, on the screen
 * where it does the most damage.
 *
 * Every control here is a filter. **There is no write control in this component and
 * no disabled one**; *Xoá bộ lọc* rewrites local state and issues a read.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { AuditLogTypeName } from '../../api/types';
import type { AuditFilters, SuccessFilter } from '../../audit/filters';

export interface AuditFilterBarProps {
  readonly draft: AuditFilters;
  readonly onChange: (patch: Partial<Omit<AuditFilters, 'cursor'>>) => void;
  readonly onApply: () => void;
  readonly onClear: () => void;
}

const TEXT_FIELDS = [
  { key: 'actorId', id: 'actor', label: 'k8.audit.filters.actor' },
  { key: 'action', id: 'action', label: 'k8.audit.filters.action' },
  { key: 'resourceType', id: 'restype', label: 'k8.audit.filters.resourceType' },
  { key: 'resourceId', id: 'resid', label: 'k8.audit.filters.resourceId' },
  { key: 'from', id: 'from', label: 'k8.audit.filters.from' },
  { key: 'to', id: 'to', label: 'k8.audit.filters.to' },
] as const;

const LOG_TYPES: readonly (AuditLogTypeName | 'any')[] = ['any', 'audit', 'data_access'];
const SUCCESS_VALUES: readonly SuccessFilter[] = ['any', 'true', 'false'];

export function AuditFilterBar({
  draft,
  onChange,
  onApply,
  onClear,
}: AuditFilterBarProps): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="k8-audit-filters-heading">
      <h2 className="wp-section__title" id="k8-audit-filters-heading" tabIndex={-1}>
        {t('k8.audit.filters.title')}
      </h2>
      <p className="wp-small wp-muted" data-testid="k8-audit-serverside">
        {t('k8.audit.filters.serverSide')}
      </p>

      <form
        className="wp-filters wp-filters--bar"
        aria-labelledby="k8-audit-filters-heading"
        onSubmit={(event) => {
          event.preventDefault();
          onApply();
        }}
      >
        <fieldset className="wp-filters__group">
          <legend className="wp-filters__legend">{t('k8.audit.filters.logType')}</legend>
          {LOG_TYPES.map((value) => (
            <label className="wp-filters__radio" key={value} htmlFor={`k8-audit-logtype-${value}`}>
              <input
                id={`k8-audit-logtype-${value}`}
                data-testid={`k8-audit-logtype-${value}`}
                type="radio"
                name="k8-audit-logtype"
                checked={draft.logType === value}
                onChange={() => {
                  onChange({ logType: value });
                }}
              />
              {t(`k8.audit.filters.logType.${value}` as MessageKey)}
            </label>
          ))}
        </fieldset>

        {TEXT_FIELDS.map((field) => (
          <div className="wp-field" key={field.key}>
            <label className="wp-field__label" htmlFor={`k8-audit-${field.id}`}>
              {t(field.label)}
            </label>
            <input
              className="wp-field__input"
              id={`k8-audit-${field.id}`}
              data-testid={`k8-audit-${field.id}`}
              type="text"
              autoComplete="off"
              value={draft[field.key]}
              onChange={(event) => {
                onChange({ [field.key]: event.target.value });
              }}
            />
            {field.key === 'action' ? (
              <p className="wp-small wp-muted" data-testid="k8-audit-action-hint">
                {t('k8.audit.filters.actionHint')}
              </p>
            ) : null}
          </div>
        ))}

        <fieldset className="wp-filters__group">
          <legend className="wp-filters__legend">{t('k8.audit.filters.success')}</legend>
          {SUCCESS_VALUES.map((value) => (
            <label className="wp-filters__radio" key={value} htmlFor={`k8-audit-success-${value}`}>
              <input
                id={`k8-audit-success-${value}`}
                data-testid={`k8-audit-success-${value}`}
                type="radio"
                name="k8-audit-success"
                checked={draft.success === value}
                onChange={() => {
                  onChange({ success: value });
                }}
              />
              {t(`k8.audit.filters.success.${value}` as MessageKey)}
            </label>
          ))}
        </fieldset>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="k8-audit-apply"
          >
            {t('k8.audit.filters.apply')}
          </button>
          <button
            className="wp-button wp-button--quiet"
            type="button"
            data-testid="k8-audit-clear"
            onClick={onClear}
          >
            {t('k8.audit.filters.clear')}
          </button>
        </div>
      </form>
    </section>
  );
}
